import { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import Button1 from "../../../components/form/Button1";
import CommonFreeTextInput from "../../../components/common/inputs/CommonFreeTextInput";
import ConfirmSettleWinnerModal from "./ConfirmSettleWinnerModal";
import { formatMoney } from "../../../features/utils/formatMoney";
import { getWorldCupGames } from "./worldCupController";
import {
  getWorldCupGameById,
  getWorldCupTeamFlagIcon,
  getWorldCupTeams,
  saveWorldCupGames,
  WORLD_CUP_TEAMS,
} from "./payload";

export default function SettleWorldCup() {
  const navigate = useNavigate();
  const { gameId } = useParams();
  const [loadingGame, setLoadingGame] = useState(true);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [game, setGame] = useState(() => getWorldCupGameById(gameId));

  useEffect(() => {
    const loadGame = async () => {
      const savedGame = getWorldCupGameById(gameId);
      if (savedGame) {
        setGame(savedGame);
        setLoadingGame(false);
        return;
      }

      setLoadingGame(true);
      const res = await getWorldCupGames({ limit: 10, offset: 0 });
      setLoadingGame(false);

      if (res.return) {
        const games = Array.isArray(res.data?.data) ? res.data.data : [];
        saveWorldCupGames(games);
        const matchedGame = games.find(
          (item) => String(item?._id || item?.id) === String(gameId),
        );
        setGame(matchedGame ?? null);
        return;
      }

      enqueueSnackbar(res.msg, { variant: "error" });
    };

    loadGame();
  }, [gameId]);

  const teams = getWorldCupTeams(game);
  const teamCount = teams.length || WORLD_CUP_TEAMS.length;
  const filteredTeams = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return teams;

    return teams.filter((team) => team.toLowerCase().includes(query));
  }, [searchQuery, teams]);

  const handleOpenConfirm = () => {
    if (!selectedTeam) {
      enqueueSnackbar("Please select the winning team", { variant: "error" });
      return;
    }

    setOpenConfirm(true);
  };

  if (loadingGame) {
    return (
      <div className="flex flex-col gap-6">
        <div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
        <div className="h-40 animate-pulse rounded-2xl bg-gray-200" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 9 }).map((_, idx) => (
            <div
              key={idx}
              className="h-16 animate-pulse rounded-xl bg-gray-200"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="flex flex-col gap-6">
        <button
          type="button"
          className="flex w-fit items-center gap-2 text-sm font-medium text-primary hover:opacity-80"
          onClick={() => navigate("/settings/world-cup")}
        >
          <Icon icon="mdi:arrow-left" className="text-lg" />
          Back to World Cup
        </button>
        <div className="rounded-2xl border border-dashed border-[#D7E2F0] bg-[#F8FAFD] px-4 py-16 text-center text-sm text-[#6D7C94]">
          Game not found. It may have already been settled.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          className="flex w-fit items-center gap-2 text-sm font-medium text-primary hover:opacity-80"
          onClick={() => navigate("/settings/world-cup")}
        >
          <Icon icon="mdi:arrow-left" className="text-lg" />
          Back to World Cup
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#E7EDF5] bg-white shadow-sm">
        <div className="relative bg-gradient-to-br from-[#7C2D12] via-[#C2410C] to-[#EA580C] px-6 py-8 text-white">
          <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-white/10" />

          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-4">
              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]">
                <Icon icon="mdi:flag-checkered" className="text-sm" />
                Settle Tournament
              </span>
              <div className="flex flex-col gap-2">
                <span className="text-3xl font-bold tracking-tight sm:text-4xl">
                  {game?.name || "FIFA World Cup 2026"}
                </span>
                <span className="max-w-2xl text-sm text-white/80 sm:text-base">
                  Select the winning nation to finalize this tournament and
                  distribute the prize pool.
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-white/20 bg-white/10 px-5 py-4 backdrop-blur-sm">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/75">
                Prize Pool
              </span>
              <span className="mt-2 block text-3xl font-extrabold sm:text-4xl">
                {formatMoney(game?.prizePoolAmount)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-lg font-semibold text-primary">
                Select Winning Team
              </span>
              <span className="text-sm text-[#6D7C94]">
                Choose one nation from the {teamCount} teams in this tournament.
              </span>
            </div>

            <div className="w-full lg:max-w-sm">
              <CommonFreeTextInput
                label="Search Teams"
                placeholder="Search by country name"
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </div>
          </div>

          {selectedTeam ? (
            <div className="flex items-center gap-3 rounded-xl border border-[#C2410C]/20 bg-[#FFF7ED] px-4 py-3">
              <Icon
                icon={getWorldCupTeamFlagIcon(selectedTeam)}
                className="h-7 w-7 shrink-0 rounded-full"
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium uppercase tracking-wide text-[#6D7C94]">
                  Selected Winner
                </span>
                <span className="text-sm font-semibold text-primary">
                  {selectedTeam}
                </span>
              </div>
            </div>
          ) : null}

          {filteredTeams.length ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredTeams.map((team) => {
                const isSelected = selectedTeam === team;

                return (
                  <button
                    key={team}
                    type="button"
                    onClick={() => setSelectedTeam(team)}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-4 text-left transition ${
                      isSelected
                        ? "border-[#C2410C] bg-[#FFF7ED] shadow-sm ring-1 ring-[#C2410C]/20"
                        : "border-[#E7EDF5] bg-[#F8FAFD] hover:border-[#D7E2F0] hover:bg-white"
                    }`}
                  >
                    <Icon
                      icon={getWorldCupTeamFlagIcon(team)}
                      className="h-8 w-8 shrink-0 rounded-full"
                    />
                    <span className="text-sm font-medium text-primary">
                      {team}
                    </span>
                    {isSelected ? (
                      <Icon
                        icon="mdi:check-circle"
                        className="ml-auto text-lg text-[#C2410C]"
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-[#D7E2F0] bg-[#F8FAFD] px-4 py-12 text-center text-sm text-[#6D7C94]">
              No teams match &quot;{searchQuery.trim()}&quot;.
            </div>
          )}

          <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50/80 px-4 py-3">
            <Icon
              icon="mdi:alert-circle-outline"
              className="mt-0.5 shrink-0 text-lg text-red-600"
            />
            <span className="text-sm leading-relaxed text-red-900">
              Settling the game will end the active tournament and process
              payouts for the selected winner. This action cannot be undone.
            </span>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button1
              variant="outlined"
              type="button"
              className="sm:max-w-[180px]"
              onClick={() => navigate("/settings/world-cup")}
            >
              Cancel
            </Button1>
            <Button1
              type="button"
              className="sm:max-w-[220px] !bg-[#C2410C] hover:!bg-[#9A3412]"
              onClick={handleOpenConfirm}
            >
              Settle Game
            </Button1>
          </div>
        </div>
      </div>

      <ConfirmSettleWinnerModal
        open={openConfirm}
        setOpen={setOpenConfirm}
        gameId={gameId}
        game={game}
        selectedTeam={selectedTeam}
        onSuccess={() => navigate("/settings/world-cup")}
      />
    </div>
  );
}
